import { SortingState } from "@tanstack/react-table";
import { Constants } from "appConstants";
import { IcoArrowRight } from "assets/icons";
import { HeadingListing, Loading, Table } from "components";

import clsx from "clsx";
import { MaskHelper } from "helpers";
import TableHelper from "helpers/TableHelper";
import { useQuerystringParams } from "hooks/querystringFilter";
import { Customer } from "models/customer/Customer";
import { CustomerFields } from "models/customer/types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CustomerService } from "services";
import { useTenant } from "store/contextTenant";
import "../styles.scss";
import { CustomerFilters, CustomerTabs } from "./types";

export default function CustomerList() {
	const navigate = useNavigate();
	const { tenant } = useTenant();
	const [currentTab, setCurrentTab] = useState<CustomerTabs>(
		CustomerTabs.INDIVIDUAL
	);
	const [customers, setCustomers] = useState<Customer[] | undefined>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [searchName, setSearchName] = useState<string>("");

	const customerColumns: any[] = [
		{
			header:
				currentTab === CustomerTabs.INDIVIDUAL
					? Constants.strings.pages.listing.customers.headers.name
					: Constants.strings.pages.listing.customers.headers.legalName,
			accessorKey: CustomerFields.NAME,
			meta: {
				columnClass: "name-column"
			},
			size: 40
		},
		{
			header:
				currentTab === CustomerTabs.INDIVIDUAL
					? Constants.strings.pages.listing.customers.headers.cpf
					: Constants.strings.pages.listing.customers.headers.cnpj,
			accessorKey: CustomerFields.DOCUMENT,
			size: 5,
			cell: (rowData: any) => MaskHelper.cpfCnpj(rowData?.getValue()),
			enableSorting: false
		},
		{
			header: () => Constants.strings.pages.listing.customers.headers.email,
			accessorKey: CustomerFields.EMAIL,
			size: 25
		},
		{
			header: () =>
				Constants.strings.pages.listing.customers.headers.contactPhone,
			accessorKey: CustomerFields.PHONE,
			size: 20
		},
		{
			header: "",
			accessorKey: "id",
			cell: (
				<div className="text-align--center align-items--center arrow-right">
					<IcoArrowRight />
				</div>
			),
			enableSorting: false,
			size: 10
		}
	];

	const [filters, setFilters] = useState<any>({
		size: 10,
		page: 0,
		name: searchName
	});

	const customerService = new CustomerService();

	const [sorting, setSorting] = useState<SortingState>([]);
	const { updateUrl } = useQuerystringParams<CustomerFilters>(
		setFilters,
		sorting,
		setSorting
	);

	const onPageChange = (selectedPage: number, pageSize: number) => {
		const newFilters = {
			...filters,
			size: pageSize,
			page: selectedPage - 1
		};
		setFilters(newFilters);
	};

	const parseParams = () => {
		const sortParam = TableHelper.sortingStateToParam(sorting);
		const filtersParams = {
			...filters,
			name: filters.search
		};
		if ("search" in filtersParams) {
			delete filtersParams.search;
		}
		return {
			...sortParam,
			...filtersParams
		};
	};

	useEffect(() => {
		const newFilters = {
			...filters,
			name: searchName
		};
		setFilters(newFilters);
	}, [searchName]);

	useEffect(() => {
		const sortParam = TableHelper.sortingStateToParam(sorting);
		const newFilters = {
			...filters,
			...sortParam
		};
		updateUrl(newFilters);
	}, [sorting]);

	useEffect(() => {
		setIsLoading(true);
		customerService
			.getList(currentTab, parseParams())
			.then((response: any) => {
				setCustomers(response.content);
				setTotalPages(response.totalPages);
			})
			.catch(() => setCustomers(undefined))
			.finally(() => setIsLoading(false));
	}, [filters, currentTab, tenant]);

	return (
		<div className="customer-page">
			<HeadingListing
				title={Constants.strings.general.customers}
				setSearchValue={setSearchName}
				showAddButton={false}
			/>
			<nav className="customer-tab-wrapper">
				<ul key="tab-individual">
					<li
						key="tab-individual"
						role="presentation"
						onClick={() => setCurrentTab(CustomerTabs.INDIVIDUAL)}
						className={clsx({
							"active-header-customer-tab":
								currentTab === CustomerTabs.INDIVIDUAL
						})}
					>
						<span className="font-body-regular-small">Pessoa Física</span>
					</li>
				</ul>
				<ul key="tab-legal">
					<li
						key="tab-legal"
						role="presentation"
						onClick={() => setCurrentTab(CustomerTabs.LEGAL)}
						className={clsx({
							"active-header-customer-tab": currentTab === CustomerTabs.LEGAL
						})}
					>
						<span className="font-body-regular-small">Pessoa Jurídica</span>
					</li>
				</ul>
			</nav>
			{!isLoading ? (
				<Table
					columns={customerColumns}
					data={customers as Customer[]}
					sorting={sorting}
					setSorting={setSorting}
					numberOfInstancesOptions={[10, 50, 100]}
					currentPage={filters.page + 1}
					pageSize={filters.size}
					totalPages={totalPages}
					onPageChange={onPageChange}
					onClickRow={(id: string) => navigate(`/backoffice/customers/${id}`)}
				/>
			) : (
				<div className="customer-list-loading">
					<Loading type="primary" size={75} />
				</div>
			)}
		</div>
	);
}
